/************
 * Service Class
 * File Name ...: requests.service.ts
 * Generated By : ianday on 10/13/23
 * Date ........: 10/13/23
 /************/
import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { Settings } from './settings';
import { ServiceRequest } from '../models/service.request';
import { Case } from '../models/case';
import { HouseholdMember } from '../models/household-member';
import { SearchQBE } from '../models/searchqbe';
import { CaseMember } from "../models/case-member";


@Injectable({
	providedIn: 'root'
})
export class ServiceRequestsService {

	public URLPath: string = Settings.getInstance().BASE_URL + 'requests/';
	public refreshEvent: EventEmitter<void> = new EventEmitter<void>();

	constructor(private http: HttpClient) {
	}
	/** Request Statuses **/
	getAllRequestStatuses(): Observable<any> {
		return this.http.get<any>(this.URLPath + 'statuses')
			.pipe(
				retry(1)
			);
	}
	getAllActiveRequestStatuses(): Observable<any> {
		return this.http.get<any>(this.URLPath + 'active-statuses')
			.pipe(
				retry(1)
			);
	}
	createRequestStatus(record: any): Observable<any> {
		return this.http.post<any>(this.URLPath + 'statuses/', record)
			.pipe(
				retry(1)
			);
	}
	updateRequestStatus(record: any): Observable<any> {
		return this.http.put(this.URLPath + 'statuses/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	deleteRequestStatus(id: number): Observable<any> {
		return this.http.delete(this.URLPath + 'statuses/' + id)
			.pipe(
				retry(1)
			);
	}
	/** Service Request Types **/
	getAllServiceRequestTypes(): Observable<any> {
		return this.http.get<any>(this.URLPath + 'request-types')
			.pipe(
				retry(1)
			);
	}
	createRequestType(record: any): Observable<any> {
		return this.http.post<any>(this.URLPath + 'request-types/', record)
			.pipe(
				retry(1)
			);
	}
	updateRequestType(record: any): Observable<any> {
		return this.http.put(this.URLPath + 'request-types/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	deleteRequestType(id: number): Observable<any> {
		return this.http.delete(this.URLPath + 'request-types/' + id)
			.pipe(
				retry(1)
			);
	}
	/** Service Requests **/
	getAllRequestsByAgencyIdAndRecordStatus(id: number, recordStatus: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/agency/' + id + '/' + recordStatus)
			.pipe(
				retry(1)
			);
	}
	getServiceRequestListRow(id: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/list-row/' + id)
			.pipe(
				retry(1)
			);
	}
	getAllServiceRequestsByRequestStatusId(id: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/status/' + id)
			.pipe(
				retry(1)
			);
	}
	getAllPastServiceRequestsByCaseId(id: number, restrictedAgencyFlag: boolean = false): Observable<any> {
		return this.http.get<any>(
			this.URLPath + `service-requests/past-requests/${id}/${restrictedAgencyFlag}`
		).pipe(retry(1));
	}

	getOpenAndOverDueServiceRequests(agencyId?: number): Observable<any> {
		let params = new HttpParams();
		if (agencyId && agencyId > 0) {
			params = params.set('agencyId', agencyId.toString());
		}

		return this.http.get<any>(this.URLPath + 'service-requests/open/overdue', { params })
			.pipe(
				retry(1)
			);
	}
	createMultipleRequests(record: ServiceRequest[]): Observable<any> {
		return this.http.post(this.URLPath + 'service-requests/', record)
			.pipe(
				retry(1)
			);
	}
	updateRequestsStatus(record: any): Observable<any> {
		return this.http.put(this.URLPath + 'service-requests/status/' + record.serviceRequest.id, record)
			.pipe(
				retry(1)
			);
	}
	updateRequestsMatch(record: ServiceRequest, emailFlag: boolean): Observable<any> {
		return this.http.put(this.URLPath + 'service-requests/match/' + record.id + '?emailFlag=' + emailFlag, record)
			.pipe(
				retry(1)
			);
	}
	updateRequestCollaboration(srId: number, collabRequest: number): Observable<any> {
		return this.http.get(this.URLPath + 'service-requests/collab-request/' + srId + '/' + collabRequest)
			.pipe(
				retry(1)
			);
	}
	updateRequests(record: ServiceRequest, sendStatusEmail: boolean = false): Observable<any> {
		return this.http.put(this.URLPath + 'service-requests/' + record.id + '?sendEmailFlag=' + sendStatusEmail, record)
			.pipe(
				retry(1)
			);
	}
	createServiceRequest(record: ServiceRequest): Observable<any> {
		return this.http.post(this.URLPath + 'service-requests/new', record)
			.pipe(
				retry(1)
			);
	}
	updateServiceRequestDefDetails(record: ServiceRequest): Observable<any> {
		return this.http.put(this.URLPath + 'service-requests/details/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	getCaseById(id: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/case/' + id)
			.pipe(
				retry(1)
			);
	}
	getCaseByCaseNumber(caseNumber: string): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/case/case/' + caseNumber)
			.pipe(
				retry(1)
			);
	}
	createCase(record: Case): Observable<any> {
		// Trim names to remove leading/trailing spaces
		record.firstName = (record.firstName || '').trim();
		record.middleName = (record.middleName || '').trim();
		record.lastName = (record.lastName || '').trim();
		return this.http.post(this.URLPath + 'service-requests/case/', record)
			.pipe(
				retry(1)
			);
	}
	updateCase(record: Case): Observable<any> {
		// Trim names to remove leading/trailing spaces
		record.firstName = (record.firstName || '').trim();
		record.middleName = (record.middleName || '').trim();
		record.lastName = (record.lastName || '').trim();
		return this.http.put(this.URLPath + 'service-requests/case/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	updateCaseCurrentNeed(record: ServiceRequest, caseId: number): Observable<any> {
		return this.http.post(this.URLPath + 'service-requests/currentneed/' + caseId, record)
			.pipe(
				retry(1)
			);
	}
	getMembersByDuplicateKey(duplKey: string): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/members-duplicated/' + duplKey)
			.pipe(
				retry(1)
			);
	}
	getHouseholdMembersByCaseId(caseId: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'service-requests/members/' + caseId)
			.pipe(
				retry(1)
			);
	}
	/** Household Members **/
	createHouseholdMember(member: HouseholdMember): Observable<any> {
		// Trim names to remove leading/trailing spaces
		member.firstName = (member.firstName || '').trim();
		member.middleName = (member.middleName || '').trim();
		member.lastName = (member.lastName || '').trim();
		return this.http.post(this.URLPath + 'members/', member)
			.pipe(
				retry(1)
			);
	}

	updateHouseholdMember(updatedMember: HouseholdMember): Observable<any> {
		// Trim names to remove leading/trailing spaces
		updatedMember.firstName = (updatedMember.firstName || '').trim();
		updatedMember.middleName = (updatedMember.middleName || '').trim();
		updatedMember.lastName = (updatedMember.lastName || '').trim();
		return this.http.put(this.URLPath + 'members/' + updatedMember.id, updatedMember)
			.pipe(
				retry(1)
			);
	}
	deleteHouseholdMember(memberId: number): Observable<any> {
		return this.http.delete(this.URLPath + 'members/' + memberId)
			.pipe(
				retry(1)
			);
	}
	unlinkHouseholdMember(cmId: number): Observable<any> {
		return this.http.delete(this.URLPath + 'case-member/' + cmId)
			.pipe(
				retry(1)
			);
	}
	createSimpleCaseMemberRecord(cm: CaseMember): Observable<any> {

		return this.http.post(this.URLPath + 'case-member/', cm)
			.pipe(
				retry(1)
			);
	}


	getSelectableRelationshipTypes(): Observable<any> {
		return this.http.get<any>(this.URLPath + 'relationship-types/')
			.pipe(
				retry(1)
			);
	}
	getParticipatingAgencyIds(srId: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'collaborations/participating-agencies/' + srId)
			.pipe(
				retry(1)
			);
	}

	reOpenClosedRequest(Id: number): Observable<any> {
		return this.http.get(this.URLPath + '/service-requests/reopen-request/' + Id)
			.pipe(
				retry(1)
			);
	}
	reOpenClosedCase(Id: number): Observable<any> {
		return this.http.get(this.URLPath + '/service-requests/reopen-case/' + Id)
			.pipe(
				retry(1)
			);
	}
	public searchServiceRequests(record: SearchQBE): Observable<any> {
		return this.http.post(this.URLPath + 'service-requests/search/', record)
			.pipe(
				retry(1)
			);
	}

	/** Status Reasons **/

	getAllActiveStatusReasons(): Observable<any> {
		return this.http.get<any>(this.URLPath + 'status-reasons/active')
			.pipe(
				retry(1)
			);
	}
	getAllStatusReasons(): Observable<any> {
		return this.http.get<any>(this.URLPath + 'status-reasons')
			.pipe(
				retry(1)
			);
	}
	createStatusReason(record: any): Observable<any> {
		return this.http.post<any>(this.URLPath + 'status-reasons/', record)
			.pipe(
				retry(1)
			);
	}
	updateStatusReason(record: any): Observable<any> {
		return this.http.put(this.URLPath + 'status-reasons/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	deleteStatusReason(id: number): Observable<any> {
		return this.http.delete(this.URLPath + 'status-reasons/' + id)
			.pipe(
				retry(1)
			);
	}
	/** Agency Responses **/
	getAgencyResponsesForServiceId(id: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'agency-responses/' + id)
			.pipe(
				retry(1)
			);
	}
	manageAgencyResponses(records: any): Observable<any> {
		return this.http.post(this.URLPath + 'agency-responses/manage/', records)
			.pipe(
				retry(1)
			);
	}

	/** Case avatar image functions **/

	getCaseAvatarImage(caseId: any): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'requests/cases/avatar/' + caseId, { responseType: 'blob' as 'json' })
			.pipe(
				retry(0)
			);
	}
	updateCaseAvatarImage(caseId: any, avatarImage: File): Observable<any> {
		const formData: FormData = new FormData();
		formData.append('avatar', avatarImage, avatarImage.name);
		return this.http.post<any>(Settings.getInstance().BASE_URL + 'cases/avatar/' + caseId, formData)
			.pipe(
				retry(0)
			);
	}
	removeCaseAvatarImage(caseId: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'requests/cases/avatar/' + caseId)
			.pipe(
				retry(1)
			);
	}
	getAllServiceRequestsByCaseId(caseId: number, restrictedAgencyFlag: boolean = false, agencyId: number = -1): Observable<any> {
		let url = `${this.URLPath}service-requests/by/case/${caseId}`;
		if (restrictedAgencyFlag) {
			url += `?restrictedAgencyFlag=${restrictedAgencyFlag}`;
			if (agencyId !== -1) {
				url += `&agencyId=${agencyId}`;
			}
		}
		return this.http.get<any>(url).pipe(retry(1));
	}

	getCustomActionMappingsByStatusId(id: number): Observable<any> {
		return this.http.get<any>(this.URLPath + 'custom-actions-mapping/status/' + id)
			.pipe(
				retry(1)
			);
	}
	createOrUpdateCustomActionsMappings(mappings: any[], requestStatusId: number): Observable<any> {
		return this.http.post<any>(`${this.URLPath}custom-actions-mapping/${requestStatusId}`, mappings)
			.pipe(
				retry(1)
			);
	}


}
